/**
 * Exact decimal helpers on BigInt (no floating point). Quantities travel as
 * strings ("12.5000") and are scaled to integers for arithmetic.
 */

const DEC_RE = /^(\d+)(?:\.(\d+))?$/;

/** Parse a non-negative decimal string into an integer scaled by 10^scale. Returns null if invalid or too precise. */
export function parseDecimal(input: string | number, scale: number): bigint | null {
  const s = String(input).trim().replace(/,/g, '');
  const m = DEC_RE.exec(s);
  if (!m) return null;
  const frac = (m[2] ?? '').replace(/0+$/, '');
  if (frac.length > scale) return null;
  return BigInt(m[1]!) * 10n ** BigInt(scale) + BigInt(frac.padEnd(scale, '0') || '0');
}

/** Format an integer scaled by 10^scale. `trim` removes trailing zeros. */
export function formatDecimal(value: bigint, scale: number, trim = false): string {
  const neg = value < 0n;
  const abs = neg ? -value : value;
  const p = 10n ** BigInt(scale);
  let frac = (abs % p).toString().padStart(scale, '0');
  if (trim) frac = frac.replace(/0+$/, '');
  return (neg ? '-' : '') + (abs / p).toString() + (frac ? '.' + frac : '');
}

/** n / d rounded half-up (n, d > 0). */
export function divRoundHalfUp(n: bigint, d: bigint): bigint {
  return (n * 2n + d) / (2n * d);
}

/** Normalise a decimal string, e.g. "1.000" -> "1", "0.450" -> "0.45". Returns null if invalid. */
export function normalizeDecimal(input: string | number, scale: number): string | null {
  const v = parseDecimal(input, scale);
  return v == null ? null : formatDecimal(v, scale, true);
}
